/**
 * Small DOM overlay in the top-left of the game stage showing the live
 * character progression: level, an XP-to-next-level bar, and gold
 * (HEX-131 / ADR-0018 §3). Sits below the discoveries HUD as part of the
 * top-left readout cluster; `App.ts` calls `update` after every event that
 * can move level / XP / gold (movement, combat reward, level-up, chest /
 * shop / event gold).
 *
 * The bar fill is derived from `xpForLevel` (ADR-0018 §3 curve): the share of
 * the current level's XP band the character has filled toward the next level.
 * At `XP_MAX_LEVEL` there is no next band — the bar reads full and the
 * to-next readout shows `MAX`.
 */

import { XP_MAX_LEVEL, levelForXp, xpForLevel } from '../../progression/xp';

export interface WorldHudState {
  /** Accumulated XP (run-scoped). Drives both the level and the bar fill. */
  xp: number;
  /** Current gold. */
  gold: number;
}

/** Fraction [0, 1] of the current level's XP band filled toward the next level. */
export function xpBarFraction(xp: number): number {
  const level = levelForXp(xp);
  if (level >= XP_MAX_LEVEL) return 1;
  const floor = xpForLevel(level);
  const ceil = xpForLevel(level + 1);
  const span = ceil - floor;
  if (span <= 0) return 1;
  const filled = (xp - floor) / span;
  return filled < 0 ? 0 : filled > 1 ? 1 : filled;
}

export class WorldHud {
  private root: HTMLDivElement | null = null;
  private levelEl: HTMLSpanElement | null = null;
  private goldEl: HTMLSpanElement | null = null;
  private barFillEl: HTMLElement | null = null;
  private barEl: HTMLDivElement | null = null;
  private state: WorldHudState = { xp: 0, gold: 0 };

  constructor(private readonly container: HTMLElement) {}

  mount(initial: WorldHudState): void {
    if (this.root) return;
    this.state = { ...initial };

    const root = document.createElement('div');
    root.className = 'hex6-world-hud';

    // Top row: LEVEL n · GOLD n
    const row = document.createElement('div');
    row.className = 'hex6-world-hud__row';

    const levelLabel = document.createElement('span');
    levelLabel.className = 'hex6-world-hud__label';
    levelLabel.textContent = 'Level';
    const levelValue = document.createElement('span');
    levelValue.className = 'hex6-world-hud__value';

    const goldLabel = document.createElement('span');
    goldLabel.className = 'hex6-world-hud__label hex6-world-hud__label--gold';
    goldLabel.textContent = 'Gold';
    const goldValue = document.createElement('span');
    goldValue.className = 'hex6-world-hud__value hex6-world-hud__value--gold';

    row.append(levelLabel, levelValue, goldLabel, goldValue);

    // XP bar (reuses the shared `.bar` chrome from theme.css).
    const bar = document.createElement('div');
    bar.className = 'hex6-world-hud__xp bar';
    const fill = document.createElement('i');
    fill.className = 'hex6-world-hud__xp-fill';
    bar.appendChild(fill);

    root.append(row, bar);
    this.container.appendChild(root);

    this.root = root;
    this.levelEl = levelValue;
    this.goldEl = goldValue;
    this.barEl = bar;
    this.barFillEl = fill;
    this.paint();
  }

  /** Lift the readout from the latest character state. Cheap no-op if unchanged. */
  update(next: WorldHudState): void {
    if (next.xp === this.state.xp && next.gold === this.state.gold) return;
    this.state = { ...next };
    this.paint();
  }

  private paint(): void {
    const level = levelForXp(this.state.xp);
    const fraction = xpBarFraction(this.state.xp);
    if (this.levelEl) this.levelEl.textContent = String(level);
    if (this.goldEl) this.goldEl.textContent = String(this.state.gold);
    if (this.barFillEl) this.barFillEl.style.transform = `scaleX(${fraction})`;
    if (this.barEl) {
      const atMax = level >= XP_MAX_LEVEL;
      const next = atMax ? xpForLevel(level) : xpForLevel(level + 1);
      this.barEl.title = atMax
        ? `Level ${level} — MAX (${this.state.xp} XP)`
        : `Level ${level} — ${this.state.xp} / ${next} XP to next`;
    }
  }

  getState(): WorldHudState {
    return { ...this.state };
  }

  destroy(): void {
    this.root?.remove();
    this.root = null;
    this.levelEl = null;
    this.goldEl = null;
    this.barEl = null;
    this.barFillEl = null;
  }
}
